import React, { useState } from 'react';
import { MessageCircle, Copy, Check, Share2 } from 'lucide-react';

interface ShareButtonProps {
  className?: string;
  variant?: 'primary' | 'secondary' | 'minimal';
  size?: 'small' | 'medium' | 'large';
}

const ShareButton: React.FC<ShareButtonProps> = ({ 
  className = '', 
  variant = 'primary',
  size = 'medium' 
}) => {
  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareMessages = [
    "Hey! I just discovered Bond Coffee Roasters in Marblehead - they literally deliver fresh roasted coffee to your door in a Japanese mini truck. I'm not even kidding. The founders are neighbors who got obsessed with coffee science and now they're personally delivering to everyone within 5 miles. Plus the more Marblehead people who join, the cheaper it gets for all of us! Check it out: " + window.location.origin,
    
    "So... I may have found the most Marblehead thing ever. Two neighbors started a coffee company and they deliver personally in what I can only describe as the cutest truck situation. Fresh roasted beans, community pricing that gets better as more locals join, and they actually know your name when they show up. It's like having coffee friends who happen to be really good at their job: " + window.location.origin,
    
    "Update from the caffeine front: Found these local coffee guys (Bond Coffee) who are doing something actually cool in Marblehead. Personal delivery, fresh roasting, and they're building this whole community pricing thing where we all save money as more neighbors join. Also, they have a Japanese mini truck. Because of course they do. Worth checking out: " + window.location.origin
  ];

  const randomMessage = shareMessages[Math.floor(Math.random() * shareMessages.length)];

  const handleCopyMessage = async () => {
    try {
      await navigator.clipboard.writeText(randomMessage);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy message:', err);
    }
  };

  const handleTextMessage = () => {
    const encodedMessage = encodeURIComponent(randomMessage);
    window.open(`sms:?body=${encodedMessage}`, '_blank');
  };

  const getButtonStyles = () => {
    const baseStyles = "inline-flex items-center gap-2 font-semibold transition-all duration-300 rounded-lg";
    
    const sizeStyles = {
      small: "px-3 py-2 text-sm",
      medium: "px-6 py-3 text-base",
      large: "px-8 py-4 text-lg"
    };

    const variantStyles = {
      primary: "bg-[#fda36b] text-[#132947] hover:bg-[#fda36b]/90 transform hover:scale-105",
      secondary: "bg-white/10 text-white hover:bg-white/20 border border-white/20 hover:border-[#fda36b]/30",
      minimal: "text-[#fda36b] hover:text-[#fda36b]/80 underline"
    };

    return `${baseStyles} ${sizeStyles[size]} ${variantStyles[variant]} ${className}`;
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className={getButtonStyles()}
      >
        <Share2 className="w-4 h-4" />
        {variant === 'minimal' ? 'Share' : 'Share Bond with Friends'}
      </button>

      {showModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 z-[9999] overflow-y-auto">
          <div className="min-h-screen flex items-center justify-center w-full">
          <div className="bg-[#132947] rounded-2xl max-w-lg w-full p-8 relative">
            <button
              onClick={() => setShowModal(false)}
             className="absolute top-3 right-3 p-3 hover:bg-white/10 rounded-lg transition-colors z-[10000] touch-manipulation"
            >
             <span className="text-white text-2xl font-bold leading-none">✕</span>
            </button>

            <div className="space-y-6">
              {/* Header */}
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-[#fda36b]/20 rounded-full flex items-center justify-center mx-auto">
                  <MessageCircle className="w-8 h-8 text-[#fda36b]" />
                </div>
                <h2 className="text-2xl font-bold text-white">
                  Spread the Coffee Love!
                </h2>
                <p className="text-white/80 leading-relaxed">
                  Help us build the Marblehead coffee community! Share Bond with your neighbors 
                  and friends - the more people who join, the better the prices get for everyone.
                </p>
              </div>

              {/* Message Preview */}
              <div className="bg-white/5 p-4 rounded-lg border border-white/10 max-h-40 overflow-y-auto">
                <h3 className="text-white font-semibold mb-3 text-sm">Your message:</h3>
                <p className="text-white/90 text-sm leading-relaxed">
                  {randomMessage}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={handleTextMessage}
                  className="w-full bg-[#fda36b] text-[#132947] px-6 py-4 rounded-lg font-semibold hover:bg-[#fda36b]/90 transition-all duration-300 flex items-center justify-center gap-3"
                >
                  <MessageCircle className="w-5 h-5" />
                  Send Text Message
                </button>

                <button
                  onClick={handleCopyMessage}
                  className="w-full bg-white/10 text-white px-6 py-4 rounded-lg font-semibold hover:bg-white/20 transition-all duration-300 flex items-center justify-center gap-3"
                >
                  {copied ? (
                    <>
                      <Check className="w-5 h-5 text-green-400" />
                      <span className="text-green-400">Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-5 h-5" />
                      Copy Message
                    </>
                  )}
                </button>
              </div>

              {/* Footer */}
              <div className="bg-[#fda36b]/10 p-4 rounded-lg border border-[#fda36b]/20">
                <p className="text-white/90 text-sm text-center leading-relaxed">
                  <span className="font-semibold text-[#fda36b]">Every neighbor counts!</span> The more people who join, 
                  the better the prices get for everyone. Let's build something amazing together.
                </p>
              </div>

              <button
                onClick={() => setShowModal(false)}
                className="w-full text-white/60 hover:text-white transition-colors text-sm"
              >
                Maybe later
              </button>
            </div>
          </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShareButton;